import { useMutation } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import { useInvalidateQueries } from "../../../services/axiosInstance";
import { GET_SAVED_PAYMENT_METHODS_QUERY_KEY } from "../api/paymentApi";
import { OwnerPaymentServiceTokenTypes } from "../types";
import axios, { AxiosResponse } from "axios";
import { ServerResponse } from "../../../types";

type PaymentResponse<TData, TMeta> = AxiosResponse<ServerResponse<TData, TMeta>>;

export function usePaymentMutation<
  TData = unknown,
  TMeta = unknown,
  TConfig = unknown,
>(
  mutationFn: (
    config: TConfig,
  ) => Promise<PaymentResponse<TData, TMeta> | void>,
  onSuccess?: (response: PaymentResponse<TData, TMeta>) => void | Promise<void>,
  onError?: (error: Error) => string | undefined,
) {
  const [waiting, setWaiting] = useState(false);
  const [cardValidationError, setCardValidationError] = useState<string>();
  const [failedCard, setFailedCard] = useState<OwnerPaymentServiceTokenTypes>();
  const pendingRef = useRef(false);

  const invalidateSavedCards = useInvalidateQueries(
    GET_SAVED_PAYMENT_METHODS_QUERY_KEY,
  );

  const { mutate: rawMutate } = useMutation({
    mutationFn: mutationFn,
    onMutate: () => {
      setCardValidationError(undefined);
      setFailedCard(undefined);
      setWaiting(true);
    },
    onSuccess: async (response) => {
      if (response) {
        await onSuccess?.(response);
      }
      await invalidateSavedCards();
      setWaiting(false);
      pendingRef.current = false;
    },
    onError: async (error) => {
      setWaiting(false);
      pendingRef.current = false;
      setCardValidationError(onError?.(error));

      if (
        axios.isAxiosError<
          ServerResponse<
            string,
            { ownerPaymentServiceToken: OwnerPaymentServiceTokenTypes }
          >
        >(error)
      ) {
        setFailedCard(error.response?.data?.meta.ownerPaymentServiceToken);
      }

      await invalidateSavedCards();
    },
  });

  // Guard against rapid double-clicks: ref is synchronous so it blocks
  // before React's async state update disables the button
  const mutate = useCallback(
    (...args: Parameters<typeof rawMutate>) => {
      if (pendingRef.current) return;
      pendingRef.current = true;
      rawMutate(...args);
    },
    [rawMutate],
  );

  return useMemo(() => {
    return {
      waiting,
      setWaiting,
      failedCard,
      cardValidationError,
      setCardValidationError,
      mutate,
    };
  }, [waiting, failedCard, cardValidationError, mutate]);
}
